import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertController, ToastController } from '@ionic/angular';

export type ErrorNotifyType = 'log' | 'toast' | 'alert-ok' | 'alert-reload' | 'alert-back';

@Injectable({
	providedIn: 'root'
})
export class ErrorService
{

	alertActive = false;
	statusCode: any;

	constructor(
		private alertController: AlertController,
		private toastController: ToastController
	) { }

	public handleError(error: any, errorNotifyType: ErrorNotifyType = 'log', overrideErrorTitle = ''): string
	{
		console.warn(error);
		if (!error)
		{
			return '';
		}

		let errorTitle = overrideErrorTitle
		let errorMessage = (error.message) ? error.message : (error ?? '');
		let statusCode = 0;

		if (error instanceof HttpErrorResponse && error.error)
		{
			console.debug('Handling HTTP error:', error);
			const httpError = error as HttpErrorResponse;
			statusCode = httpError.status;
			// Sometimes the error details are in different places
			if (httpError.error.error)
			{
				errorTitle = (httpError.error.error.title) ? httpError.error.error.title : errorTitle;
				errorMessage = (httpError.error.error.detail) ? ' ' + httpError.error.error.detail : errorMessage;
				errorMessage = (httpError.error.error.error_description) ? ' ' + httpError.error.error.error_description : errorMessage;
				errorMessage = (httpError.error.error.message) ? ' ' + httpError.error.error.message : errorMessage;

				if (errorMessage.startsWith("Unable to connect to the PHarmacy Api"))
				{
					errorTitle = 'Connection Error';
					errorMessage = "Your pharmacy appears to be offline at present. Please try again later.";
				}
			}
			else if (httpError.error)
			{
				errorTitle = (httpError.error.title) ? httpError.error.title : errorTitle;
				errorMessage = (httpError.error.detail) ? ' ' + httpError.error.detail : errorMessage;
			}
		}
		else if (error.status)
		{
			statusCode = parseInt(error.status, 10);
		}

		if (!errorMessage)
		{
			if (error.Message)
			{
				errorMessage = error.Message;
			}
			if (error.detail)
			{
				errorMessage = error.detail;
			}
		}

		if (statusCode)
		{
			switch (statusCode)
			{
				case 404:
					if (!errorTitle) errorTitle = errorTitle ? errorTitle : 'Not Found Error';
					if (!errorMessage) errorMessage = 'Resource not found ' + (error.url ?? '');
					errorNotifyType = 'alert-reload';
					break;
				case 400:
					// Handle OAuth/OpenID errors with specific messages
					if (error instanceof HttpErrorResponse && error.error)
					{
						const err = error.error;
						errorTitle = err.error || 'Bad Request';
						errorMessage = err.error_description || 'There was a problem sending information to our systems.';
					}
					else
					{
						if (!errorTitle) errorTitle = 'Bad Request';
						if (!errorMessage) errorMessage = 'There was a problem sending information to our systems. ' + (error.url ?? '');
					}
					break;
				case 422:
					if (!errorTitle) errorTitle = 'Validation Error';
					if (!errorMessage) errorMessage = 'There was a problem sending information to our systems. ' + (error.url ?? '');
					break;
				case 401:
					if (!errorTitle) errorTitle = 'Security Error';
					if (!errorMessage) errorMessage = 'You are not authorised to fetch this information. ' + (error.url ?? '');
					break;
				case 403:
					if (!errorTitle) errorTitle = 'Security Error';
					if (!errorMessage) errorMessage = 'You do not have permission to perform this action. ' + (error.url ?? '');
					break;
				case 500:
					errorTitle = 'System Error';
					if (!errorMessage) errorMessage = 'There was a problem with our systems. Please try again later. ' + (error.url ?? '');
					break;
				default:
					errorTitle = 'Error';
					if (error.name === 'TimeoutError')
					{
						errorTitle = errorTitle ? errorTitle : 'Timeout Error';
						errorMessage = (errorMessage) ? errorMessage : 'Unable to load information from our systems in a timely manner. There may be a problem with your internet connection, or with our systems.<br/><br/>If this error continues please contact our support team.';
					} else if (error.name === 'HttpErrorResponse')
					{
						errorTitle = errorTitle ? errorTitle : 'Resource Error';
						errorMessage = (errorMessage) ? errorMessage : 'Failed to connect to our systems. Please check your internet connection. If this error continues please contact our support team.';
					}
					break;
			}
		}

		this.statusCode = statusCode;

		if (!errorTitle)
		{
			errorTitle = 'Error';
		}
		console.error(errorTitle, errorMessage);

		if (!navigator.onLine)
		{
			this.presentDeviceOfflineToast();
		}
		else if (errorNotifyType === 'toast')
		{
			this.presentToast(errorTitle + (errorTitle ? ' - ' : '') + errorMessage);
		}
		else if (!this.alertActive && errorNotifyType !== 'log')
		{
			this.alertActive = true;
			this.presentAlert(errorTitle, errorMessage, errorNotifyType);
		}

		return `${errorTitle}: ${errorMessage}`;
	}

	public async presentToast(message: string, color: string = 'danger', duration: number = 5000, position: 'bottom' | 'top' = 'bottom')
	{
		const toast = await this.toastController.create({
			message,
			duration,
			position,
			animated: true,
			color,
			translucent: false,
			buttons: [
				{
					side: 'end',
					icon: 'close-circle-outline',
					role: 'cancel',
					handler: () =>
					{
						toast.dismiss();
					}
				}
			],
		});
		toast.present();
	}

	public async presentAlert(header: string, message: string, errorNotifyType: ErrorNotifyType, timeout = 300000)
	{
		const buttons = [];

		buttons.push({
			text: 'OK',
			role: 'cancel',
			cssClass: errorNotifyType === 'alert-ok' ? 'primary' : 'secondary',
			handler: () =>
			{
				this.alertActive = false;
			}
		});
		if (errorNotifyType === 'alert-back')
		{
			buttons.push({
				text: 'Go Back',
				role: '',
				cssClass: 'secondary',
				handler: () =>
				{
					this.alertActive = false;
					history.go(-1);
				}
			});
		}

		if (errorNotifyType === 'alert-reload')
		{
			buttons.push({
				text: 'Reload',
				role: '',
				cssClass: 'secondary',
				handler: () =>
				{
					this.alertActive = false;
					location.reload();
				}
			});
		}

		const alert = await this.alertController.create({
			header,
			mode: 'md',
			message,
			buttons
		});

		await alert.present();

		setTimeout(() =>
		{
			this.alertActive = false;
			alert.dismiss();
		}, timeout);
	}

	public presentDeviceOfflineToast()
	{
		const errorTitle = 'No Internet Connection';
		const errorMessage = 'Your device appears to be offline.<br/><br/>This app needs an internet connection in order to connect to our systems.';
		this.presentToast(errorTitle + (errorTitle ? '<br/><br/>' : '') + errorMessage, 'danger');
	}
}
